import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../utils/DateUtils';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { IconButton } from '@mui/material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const typeToPolish = {
    league: 'Liga',
    cup_group: 'Puchar Ligi',
};

const groupToPolish = {
    A: 'Grupa A',
    B: 'Grupa B',
};

const getMatchHeader = (match) => {
    if (match.type === 'league') return `${typeToPolish.league}, kolejka ${match.gameweek}`;
    if (match.type === 'cup_group') return `${typeToPolish.cup_group}, kolejka ${match.cup_gameweek}, ${groupToPolish[match.group] || 'grupa'}`;
    if (match.type === 'cup_knockout') return match.stage ? `${typeToPolish.cup_group}, ${match.stage}` : typeToPolish.cup_group;
    return `Kolejka ${match.gameweek}`;
};

const FixturesView = ({ season }) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [teams, setTeams] = useState([]);
    const [form, setForm] = useState({
        type: 'league',
        group: '',
        gameweek: '',
        home_team_id: '',
        away_team_id: '',
        date: ''
    });
    const [error, setError] = useState(null);
    const [fixtures, setFixtures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ id: null, home_team_id: '', away_team_id: '', date: '' });
    const [editError, setEditError] = useState(null);
    const [bulkModal, setBulkModal] = useState(null); // { header, matchIds }
    const [bulkDate, setBulkDate] = useState('');
    const [bulkError, setBulkError] = useState(null);

    const fetchFixtures = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BACKEND_URL}/matches/fixtures?season=${season}`);
            setFixtures(response.data);
        } catch {
            setFixtures([]);
        } finally {
            setLoading(false);
        }
    }, [season]);

    useEffect(() => {
        fetchFixtures();
    }, [fetchFixtures]);

    const handleRowClick = (matchId) => {
        navigate(`/mecz/${matchId}`);
    };

    const handleShowModal = async () => {
        setError(null);
        setForm({
            gameweek: '',
            home_team_id: '',
            away_team_id: '',
            date: ''
        });
        try {
            const response = await axios.get(`${BACKEND_URL}/teams?season=${season}`);
            setTeams(response.data);
            setShowModal(true);
        } catch (err) {
            setError('Błąd podczas pobierania drużyn.');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => {
            // Reset group if type changes to league
            if (name === 'type' && value === 'league') {
                return { ...prev, [name]: value, group: '' };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        // Validation: group required for cup_group
        if (form.type === 'cup_group' && !form.group) {
            setError('Wybierz grupę dla meczu grupowego.');
            return;
        }
        try {
            await axios.post(`${BACKEND_URL}/matches/add`, { ...form, season }, { withCredentials: true });
            setShowModal(false);
            fetchFixtures();
        } catch (err) {
            setError('Błąd podczas dodawania meczu.');
        }
    };

    const handleShowEditModal = async (match, e) => {
        e.stopPropagation();
        setEditError(null);
        try {
            const response = await axios.get(`${BACKEND_URL}/teams?season=${season}`);
            setTeams(response.data);
            setEditForm({
                id: match.id,
                home_team_id: match.home_team_id || '',
                away_team_id: match.away_team_id || '',
                date: match.date ? match.date.slice(0, 16) : ''
            });
            setShowEditModal(true);
        } catch (err) {
            setEditError('Błąd podczas pobierania drużyn.');
        }
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleShowBulk = (group, e) => {
        e.stopPropagation();
        setBulkError(null);
        // Prefill with the first match's date if it has one.
        const firstDated = group.matches.find(m => m.date);
        setBulkDate(firstDated ? firstDated.date.slice(0, 16) : '');
        setBulkModal({ header: group.header, matchIds: group.matches.map(m => m.id) });
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        setBulkError(null);
        try {
            await axios.post(`${BACKEND_URL}/matches/bulk-date`, {
                match_ids: bulkModal.matchIds,
                date: bulkDate || null
            }, { withCredentials: true });
            setBulkModal(null);
            fetchFixtures();
        } catch {
            setBulkError('Błąd podczas ustawiania daty kolejki.');
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditError(null);
        try {
            await axios.put(`${BACKEND_URL}/matches/update/${editForm.id}`, {
                home_team_id: editForm.home_team_id || null,
                away_team_id: editForm.away_team_id || null,
                date: editForm.date || null
            }, { withCredentials: true });
            setShowEditModal(false);
            fetchFixtures();
        } catch (err) {
            setEditError('Błąd podczas edycji meczu.');
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Ładowanie...</span>
                </Spinner>
            </div>
        );
    }

    // Group consecutive matches with the same header
    const groupedFixtures = fixtures.reduce((acc, match) => {
        const header = getMatchHeader(match);
        const lastGroup = acc[acc.length - 1];
        if (lastGroup && lastGroup.header === header) {
            lastGroup.matches.push(match);
        } else {
            acc.push({ header, matches: [match] });
        }
        return acc;
    }, []);

    return (
        <div className="cards-container">
            {isAuthenticated && (
                <Button variant="primary" className="mb-3" onClick={handleShowModal}>
                    Dodaj mecz
                </Button>
            )}

            {groupedFixtures.length === 0 && (
                <p className="text-muted">Brak zaplanowanych meczów</p>
            )}

            {groupedFixtures.map((group, index) => (
                <Card key={index} className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <span>{group.header}</span>
                        {isAuthenticated && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={(e) => handleShowBulk(group, e)}
                                title="Ustaw datę dla całej kolejki"
                            >
                                Ustaw datę kolejki
                            </Button>
                        )}
                    </Card.Header>
                    <Card.Body>
                        <Table striped bordered hover className="mb-0" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Gospodarze</th>
                                    <th>Goście</th>
                                    {isAuthenticated && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {group.matches.map((match) => (
                                    <tr key={match.id} onClick={() => handleRowClick(match.id)} style={{ cursor: 'pointer' }}>
                                        <td>{match.date ? formatDateTime(match.date) : ''}</td>
                                        <td>{match.home_team}</td>
                                        <td>{match.away_team}</td>
                                        {isAuthenticated && (
                                            <td>
                                                <IconButton size="small" onClick={(e) => handleShowEditModal(match, e)}>
                                                    <EditNoteIcon fontSize="small" />
                                                </IconButton>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            ))}

            <Modal show={!!bulkModal} onHide={() => { setBulkModal(null); setBulkError(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Data kolejki</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {bulkError && <div className="text-danger mb-2">{bulkError}</div>}
                    <p className="text-muted">
                        {bulkModal?.header} — ustawiona data zostanie przypisana do wszystkich{' '}
                        {bulkModal?.matchIds.length} meczów tej kolejki.
                    </p>
                    <Form onSubmit={handleBulkSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Data i godzina</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={bulkDate}
                                onChange={(e) => setBulkDate(e.target.value)}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit">
                                Zapisz dla kolejki
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditError(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Edytuj mecz</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editError && <div className="text-danger mb-2">{editError}</div>}
                    <Form onSubmit={handleEditSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Gospodarze</Form.Label>
                            <Form.Select
                                name="home_team_id"
                                value={editForm.home_team_id}
                                onChange={handleEditFormChange}
                            >
                                <option value="">Wybierz drużynę</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Goście</Form.Label>
                            <Form.Select
                                name="away_team_id"
                                value={editForm.away_team_id}
                                onChange={handleEditFormChange}
                            >
                                <option value="">Wybierz drużynę</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Data i godzina</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                name="date"
                                value={editForm.date}
                                onChange={handleEditFormChange}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit">
                                Zapisz
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Dodaj mecz</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <div className="text-danger mb-2">{error}</div>}
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Typ meczu</Form.Label>
                            <Form.Select
                                name="type"
                                value={form.type}
                                onChange={handleFormChange}
                                required
                            >
                                <option value="league">Liga</option>
                                <option value="cup_group">Puchar Ligi (grupa)</option>
                            </Form.Select>
                        </Form.Group>
                        {form.type === 'cup_group' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Grupa</Form.Label>
                                <Form.Select
                                    name="group"
                                    value={form.group}
                                    onChange={handleFormChange}
                                    required
                                >
                                    <option value="">Wybierz grupę</option>
                                    <option value="A">Grupa A</option>
                                    <option value="B">Grupa B</option>
                                </Form.Select>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Kolejka</Form.Label>
                            <Form.Control
                                type="number"
                                name="gameweek"
                                value={form.gameweek}
                                onChange={handleFormChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Gospodarze</Form.Label>
                            <Form.Select
                                name="home_team_id"
                                value={form.home_team_id}
                                onChange={handleFormChange}
                                required
                            >
                                <option value="">Wybierz drużynę</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Goście</Form.Label>
                            <Form.Select
                                name="away_team_id"
                                value={form.away_team_id}
                                onChange={handleFormChange}
                                required
                            >
                                <option value="">Wybierz drużynę</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Data i godzina</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                name="date"
                                value={form.date}
                                onChange={handleFormChange}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit">
                                Dodaj
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default FixturesView;